import { produce } from 'immer';

import { RESET_STATE } from 'store/global/actions';

import { CRM_ACTION_TYPE } from '../actions';

const initialState = {
  contacts: [],
  contactsTotalCount: 0,
  selectedContact: null,
  tags: [],
  contactNotes: [],
  contactActivityLog: [],
  contactEmails: [],
  contactApplicationLinks: [],
  contactTasks: [],
  contactPropertyDefinitions: [],
  contactPropertyValues: [],
  contactChildren: [],
  contactSiblings: [],
  contactSubCompanyLinks: [],
};

const crmReducer = (state = initialState, { type, payload }) =>
  produce(state, draft => {
    switch (type) {
      case CRM_ACTION_TYPE.GET_CRM_CONTACTS:
        draft.contacts = payload.contacts;
        draft.contactsTotalCount = payload.totalCount;
        break;

      case CRM_ACTION_TYPE.GET_CRM_CONTACT_BY_ID:
        draft.selectedContact = payload;
        break;

      case CRM_ACTION_TYPE.CREATE_CRM_CONTACT:
        draft.contacts = [...state.contacts, payload];
        draft.contactsTotalCount = state.contactsTotalCount + 1;
        break;

      case CRM_ACTION_TYPE.UPDATE_CRM_CONTACT:
        draft.contacts = state.contacts.map(contact =>
          contact.id === payload.id ? payload : contact,
        );
        draft.selectedContact = payload;
        break;

      case CRM_ACTION_TYPE.DELETE_CRM_CONTACT:
        draft.contacts = state.contacts.filter(({ id }) => id !== payload);
        draft.contactsTotalCount = Math.max(state.contactsTotalCount - 1, 0);
        draft.selectedContact = null;
        break;

      case CRM_ACTION_TYPE.GET_CRM_TAGS:
        draft.tags = payload;
        break;

      case CRM_ACTION_TYPE.CREATE_CRM_TAG:
        draft.tags = [...state.tags, payload];
        break;

      case CRM_ACTION_TYPE.UPDATE_CRM_TAG:
        draft.tags = state.tags.map(tag => (tag.id === payload.id ? payload : tag));
        break;

      case CRM_ACTION_TYPE.DELETE_CRM_TAG:
        draft.tags = state.tags.filter(({ id }) => id !== payload);
        break;

      case CRM_ACTION_TYPE.GET_CRM_CONTACTS_BY_APPLICATION:
      case CRM_ACTION_TYPE.GET_CRM_CONTACTS_BY_LEAD:
        draft.contacts = payload;
        break;

      case CRM_ACTION_TYPE.GET_CONTACT_NOTES:
        draft.contactNotes = payload;
        break;

      case CRM_ACTION_TYPE.CREATE_CONTACT_NOTE:
        draft.contactNotes = [payload, ...state.contactNotes];
        break;

      case CRM_ACTION_TYPE.UPDATE_CONTACT_NOTE:
        draft.contactNotes = state.contactNotes.map(note =>
          note.id === payload.id ? payload : note,
        );
        break;

      case CRM_ACTION_TYPE.DELETE_CONTACT_NOTE:
        draft.contactNotes = state.contactNotes.filter(({ id }) => id !== payload);
        break;

      case CRM_ACTION_TYPE.GET_CONTACT_ACTIVITY_LOG:
        draft.contactActivityLog = payload?.items ?? (Array.isArray(payload) ? payload : []);
        break;

      case CRM_ACTION_TYPE.GET_CONTACT_EMAILS:
        draft.contactEmails = payload?.data ?? (Array.isArray(payload) ? payload : []);
        break;

      case CRM_ACTION_TYPE.GET_CONTACT_APPLICATION_LINKS:
        draft.contactApplicationLinks = payload;
        break;

      case CRM_ACTION_TYPE.UNLINK_CONTACT_FROM_APPLICATION:
        draft.contactApplicationLinks = state.contactApplicationLinks.filter(
          link => link.applicationId !== payload,
        );
        break;

      case CRM_ACTION_TYPE.GET_CONTACT_TASKS:
        draft.contactTasks = payload.tasks;
        break;

      case CRM_ACTION_TYPE.CREATE_CONTACT_TASK:
        draft.contactTasks = [...state.contactTasks, payload.task];
        break;

      case CRM_ACTION_TYPE.UPDATE_CONTACT_TASK:
      case CRM_ACTION_TYPE.COMPLETE_CONTACT_TASK:
        draft.contactTasks = state.contactTasks.map(task =>
          task.taskId === (payload.task?.taskId ?? payload.taskId) ? payload.task : task,
        );
        break;

      case CRM_ACTION_TYPE.DELETE_CONTACT_TASK:
        draft.contactTasks = state.contactTasks.filter(task => task.taskId !== payload.taskId);
        break;

      case CRM_ACTION_TYPE.GET_CONTACT_CHILDREN:
        draft.contactChildren = payload.items;
        break;

      case CRM_ACTION_TYPE.GET_CONTACT_SIBLINGS:
        draft.contactSiblings = payload;
        break;

      case CRM_ACTION_TYPE.GET_CONTACT_SUB_COMPANY_LINKS:
        draft.contactSubCompanyLinks = payload;
        break;

      case CRM_ACTION_TYPE.LINK_CONTACT_TO_SUB_COMPANY:
        if (payload) {
          draft.contactSubCompanyLinks = [...state.contactSubCompanyLinks, payload];
        }

        break;

      case CRM_ACTION_TYPE.UNLINK_CONTACT_FROM_SUB_COMPANY:
        draft.contactSubCompanyLinks = state.contactSubCompanyLinks.filter(
          link => link.subCompanyId !== payload,
        );
        break;

      case CRM_ACTION_TYPE.SET_CONTACT_CUSTOM_FIELD_DEFINITIONS:
        draft.contactPropertyDefinitions = payload;
        break;

      case CRM_ACTION_TYPE.ADD_CONTACT_CUSTOM_FIELD_DEFINITION:
        draft.contactPropertyDefinitions = [...state.contactPropertyDefinitions, payload];
        break;

      case CRM_ACTION_TYPE.UPDATE_CONTACT_CUSTOM_FIELD_DEFINITION:
        draft.contactPropertyDefinitions = state.contactPropertyDefinitions.map(def =>
          def.id === payload.id ? payload : def,
        );
        break;

      case CRM_ACTION_TYPE.DELETE_CONTACT_CUSTOM_FIELD_DEFINITION:
        draft.contactPropertyDefinitions = state.contactPropertyDefinitions.filter(
          def => def.id !== payload,
        );
        break;

      case CRM_ACTION_TYPE.UPSERT_CONTACT_CUSTOM_FIELD_DEFINITION_OVERRIDE:
        draft.contactPropertyDefinitions = state.contactPropertyDefinitions.map(def =>
          def.id === payload.id ? { ...def, localOverride: payload.override } : def,
        );
        break;

      case CRM_ACTION_TYPE.DELETE_CONTACT_CUSTOM_FIELD_DEFINITION_OVERRIDE:
        draft.contactPropertyDefinitions = state.contactPropertyDefinitions.map(def =>
          def.id === payload.id ? { ...def, localOverride: null } : def,
        );
        break;

      case CRM_ACTION_TYPE.SET_CONTACT_CUSTOM_FIELD_VALUES:
        draft.contactPropertyValues = payload;
        break;

      case CRM_ACTION_TYPE.RESET_CRM_STATE:
      case RESET_STATE:
        return initialState;

      default:
        break;
    }
  });

export default crmReducer;
